import {
  TimeSlot,
  OrderDispatchOption,
  DeliveryAddress,
  CartItemLegacy,
  CheckoutDeliveryAddress,
  Price,
} from "~/types";
import { AppState } from ".";
import * as deliveryAddress from "./delivery-address/reducer";
import * as ordering from "./ordering/reducer";

export function getDeliveryAddress(state: AppState): DeliveryAddress | null {
  return deliveryAddress.getDeliveryAddress(state.deliveryAddress);
}

export function getDeliveryAddresses(state: AppState): DeliveryAddress[] {
  return deliveryAddress.getDeliveryAddresses(state.deliveryAddress);
}

export function getOrderingIsLoading(
  state: AppState,
  locationRef: string,
): boolean {
  return ordering.getIsLoading(state.ordering, locationRef);
}

export function getOrderingError(
  state: AppState,
  locationRef: string,
): string | null {
  return ordering.getError(state.ordering, locationRef);
}

export function getCart(
  state: AppState,
  locationRef: string,
): CartItemLegacy[] {
  return ordering.getCart(state.ordering, locationRef);
}

export function getNotes(state: AppState, locationRef: string): string {
  return ordering.getNotes(state.ordering, locationRef);
}

export function getOrderingMetadata(
  state: AppState,
  locationRef: string,
): ordering.OrderingMetadata | null {
  return ordering.getOrderingMetadata(state.ordering, locationRef);
}

export function getServiceRef(state: AppState, locationRef: string): string {
  return ordering.getServiceRef(state.ordering, locationRef);
}

export function getVoucherCode(state: AppState): string {
  return ordering.getVoucherCode(state.ordering);
}

export function getOrderingDeliveryAddress(
  state: AppState,
  locationRef: string,
): CheckoutDeliveryAddress | null {
  return ordering.getDeliveryAddress(state.ordering, locationRef);
}

export function getCartItem(
  state: AppState,
  locationRef: string,
  cartItemId: string,
): CartItemLegacy | undefined {
  return ordering.getCartItem(state.ordering, locationRef, cartItemId);
}

export function getWaitTimeForDispatchType(
  state: AppState,
  locationRef: string,
  dispatchType: OrderDispatchOption | null,
): string | null {
  switch (dispatchType) {
    case "DELIVERY":
      return getDeliveryTime(state, locationRef);
    case "PICK_UP":
      return getPickupWaitTime(state, locationRef);
    case "SERVICE":
      return getServiceWaitTime(state, locationRef);
  }
  return null;
}

export function getPickupWaitTime(
  state: AppState,
  locationRef: string,
): string | null {
  return ordering.getPickupWaitTime(state.ordering, locationRef);
}

export function getDeliveryTime(
  state: AppState,
  locationRef: string,
): string | null {
  return ordering.getDeliveryTime(state.ordering, locationRef);
}

export function getDeliveryFee(
  state: AppState,
  locationRef: string,
): number | null {
  return ordering.getDeliveryFee(state.ordering, locationRef);
}

export function getDeliveryPrice(
  state: AppState,
  locationRef: string,
): Price | null {
  return ordering.getDeliveryPrice(state.ordering, locationRef);
}

export function getMinimumAmount(
  state: AppState,
  locationRef: string,
): number | null {
  return ordering.getMinimumAmount(state.ordering, locationRef);
}

export function getOrderDispatchOption(
  state: AppState,
  locationRef: string,
): OrderDispatchOption | null {
  return ordering.getOrderDispatchOption(state.ordering, locationRef);
}

export function getOrderServiceChargeRemoved(state: AppState): boolean {
  return ordering.getOrderServiceChargeRemoved(state.ordering);
}

export function getAvailableOrderDispatchOptions(
  state: AppState,
  locationRef: string,
): OrderDispatchOption[] {
  return ordering.getAvailableOrderDispatchOptions(state.ordering, locationRef);
}

export function getDeliveryActive(
  state: AppState,
  locationRef: string,
): boolean | undefined {
  return ordering.getDeliveryActive(state.ordering, locationRef);
}

export function getPickupActive(
  state: AppState,
  locationRef: string,
): boolean | undefined {
  return ordering.getPickupActive(state.ordering, locationRef);
}

export function getServiceActive(
  state: AppState,
  locationRef: string,
): boolean | undefined {
  return ordering.getServiceActive(state.ordering, locationRef);
}

export function getServiceValidRefs(
  state: AppState,
  locationRef: string,
): { ref: string }[] | null | undefined {
  return ordering.getServiceValidRefs(state.ordering, locationRef);
}

export function getServiceWaitTime(
  state: AppState,
  locationRef: string,
): string | null {
  return ordering.getServiceWaitTime(state.ordering, locationRef);
}

export function getServiceChargeLabel(
  state: AppState,
  locationRef: string,
): string {
  return ordering.getServiceChargeLabel(state.ordering, locationRef);
}

export function getServiceChargeRate(
  state: AppState,
  locationRef: string,
): number {
  return ordering.getServiceChargeRate(state.ordering, locationRef);
}

export function allowOrderNotes(
  state: AppState,
  locationRef: string,
): boolean | undefined {
  return ordering.allowOrderNotes(state.ordering, locationRef);
}

export function getOrderNotesMaxLength(
  state: AppState,
  locationRef: string,
): number | undefined {
  return ordering.getOrderNotesMaxLength(state.ordering, locationRef);
}

export function allowItemNotes(
  state: AppState,
  locationRef: string,
): boolean | undefined {
  return ordering.allowItemNotes(state.ordering, locationRef);
}

export function getItemNotesMaxLength(
  state: AppState,
  locationRef: string,
): number | undefined {
  return ordering.getItemNotesMaxLength(state.ordering, locationRef);
}

export function getTipsEnabledForDispatchType(
  state: AppState,
  locationRef: string,
  dispatchType: OrderDispatchOption | null,
): boolean {
  switch (dispatchType) {
    case "DELIVERY":
      return ordering.getTipsEnabledDelivery(state.ordering, locationRef);
    case "PICK_UP":
      return ordering.getTipsEnabledPickup(state.ordering, locationRef);
    case "SERVICE":
      return ordering.getTipsEnabledService(state.ordering, locationRef);
  }
  return false;
}

export function getTipRatiosForDispatchType(
  state: AppState,
  locationRef: string,
  dispatchType: OrderDispatchOption | null,
): number[] {
  switch (dispatchType) {
    case "DELIVERY":
      return ordering.getTipRatiosDelivery(state.ordering, locationRef);
    case "PICK_UP":
      return ordering.getTipRatiosPickup(state.ordering, locationRef);
    case "SERVICE":
      return ordering.getTipRatiosService(state.ordering, locationRef);
  }
  return [];
}

export function getTimeSlotsForDispatchType(
  state: AppState,
  locationRef: string,
  dispatchType: OrderDispatchOption | null,
): TimeSlot[] | null {
  switch (dispatchType) {
    case "DELIVERY":
      return ordering.getTimeSlotsDelivery(state.ordering, locationRef);
    case "PICK_UP":
      return ordering.getTimeSlotsPickup(state.ordering, locationRef);
  }
  return [];
}

export function getExternalPaymentEnabledForDispatchType(
  state: AppState,
  locationRef: string,
  dispatchType: OrderDispatchOption | null,
): boolean {
  switch (dispatchType) {
    case "DELIVERY":
      return ordering.getExternalPaymentEnabledDelivery(
        state.ordering,
        locationRef,
      );
    case "PICK_UP":
      return ordering.getExternalPaymentEnabledPickup(
        state.ordering,
        locationRef,
      );
    case "SERVICE":
      return ordering.getExternalPaymentEnabledService(
        state.ordering,
        locationRef,
      );
  }
  return false;
}

export function getSelectedDispatchTime(state: AppState): TimeSlot | null {
  return state.ordering.dispatchTime;
}
